import { Component, EventEmitter, input, Input, model, Output, signal } from '@angular/core';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule} from '@angular/material/icon';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { BallotListService, EventSortOrder } from 'src/app/ballot-list/ballot-list.service';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Ballot } from 'src/app/ballot/backend-types';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ElectorDataService } from 'src/app/Services/elector-data.service';

@Component({
    selector: 'app-petitions-item',
    imports: [MatExpansionModule, MatIconModule, CommonModule, FormsModule, MatButtonModule, MatFormFieldModule, MatSelectModule, MatCheckboxModule, MatCardModule, MatDividerModule, MatInputModule, MatTooltipModule],
    templateUrl: './petitions-item.component.html',
    styleUrl: './petitions-item.component.scss'
})
export class PetitionsItemComponent {
  title = input.required<string>()
  openDescription = input.required<string>()
  closedDescription = input.required<string>()
  @Input() ballots: any[] = [];
  @Output() selectBallot = new EventEmitter<number>()
  isOpen = signal(false);

  searchTerm = '';
  showSearchInput = false;
  searchPlaceholder = $localize`Search`;

  sortOrder: EventSortOrder = 'NEWEST_FIRST';

  showFilters: boolean = false;
  readonly separateInactive = model(true);
  readonly excludeInactive = model(false);

  tooltipTextForSSFIcon = $localize`Search, sort, and filter petitions.`;
  tooltipTextForSearch = $localize`Search petitions by title and description. Letters, numbers, and some other characters are allowed. Maximum 40 characters.`;
  tooltipTextForSort = $localize`Sort the petitions in order from newest to oldest (default), oldest to newest, or by the date and time the petitions close: soonest to latest or latest to soonest.`;
  tooltipTextForFilter = $localize`There are two filters. The first box moves all inactive petitions (submitted petitions and closed petitions) to the end of the list. The second box hides all inactive petitions.`;
  tooltipTextForSeparateEvents = $localize`Move all inactive petitions to the end of the list of petitions.`;
  tooltipTextForExcludeEvents = $localize`Only show petitions that you can sign and submit. (Hide all inactive petitions.)`;

  constructor( 
    private announcer: LiveAnnouncer, 
    public ballotListService: BallotListService, 
    private readonly userService: ElectorDataService
  ) { }

  eventImage(ballot: Ballot): string {
    return this.userService.getImageUrlForEvent(ballot) ?? "/assets/images/logo.svg";
  }

  onOpen() {
    this.isOpen.set(true) 
    this.announcer.announce(` ${this.ballots.length} ${this.title} available in list.`, 'assertive');
  }

  onClose() {
    this.isOpen.set(false) 
  }

  toggleSearch() {
    if (this.showSearchInput) {
      this.searchTerm = '';
    }
    this.showSearchInput = !this.showSearchInput;
  }

  changeSortOrder(order: EventSortOrder) {
    this.sortOrder = order;
  }

  toggleFilterVisibility(): void {
    this.showFilters = !this.showFilters;
  }

  private filterBallots(): Ballot[] {
    let includedBallots = this.ballots;
    if(this.excludeInactive()) {
      includedBallots = this.ballots.filter((b) => this.ballotListService.isActionable(b))
    }

    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return [...includedBallots];
    }

    const keywords = term.split(/\s+/); 
  
    return includedBallots.filter(b => {
      const d0 = b.voteEventData.description?.toLowerCase() || '';
      const d1 = b.voteEventData.description1?.toLowerCase() || '';
      const d2 = b.voteEventData.description2?.toLowerCase() || '';
  
      return keywords.every(keyword =>
        d0.includes(keyword) || d1.includes(keyword) || d2.includes(keyword)
      );
    });
  }

  private sortBallots(ballots: Ballot[]): Ballot[] {
    if(['NEWEST_FIRST', 'OLDEST_FIRST'].includes(this.sortOrder)) {
      return ballots.sort((a, b) => {
        const dateA = new Date(a.eventCreation).getTime();
        const dateB = new Date(b.eventCreation).getTime();
        return this.sortOrder === 'OLDEST_FIRST' ? dateA - dateB : dateB - dateA;
      });
    } else {
      return ballots.sort((a, b) => {
        const dateA = new Date(a.eventExpiration).getTime();
        const dateB = new Date(b.eventExpiration).getTime();
        return this.sortOrder === 'EXPIRATION_ASCENDING' ? dateA - dateB : dateB - dateA;
      });
    }
  }

  private separateInactiveBallotsAndSort(ballots: Ballot[]): Ballot[] {
    let activeBallots = ballots.filter((b) => this.ballotListService.isActionable(b));
    let inactiveBallots = ballots.filter((b) => !this.ballotListService.isActionable(b));

    return this.sortBallots(activeBallots).concat(this.sortBallots(inactiveBallots));
  }

  get filteredAndSortedBallots(): Ballot[] {
    const filtered = this.filterBallots();
    if(this.separateInactive()) {
      return this.separateInactiveBallotsAndSort(filtered);
    } else {
      return this.sortBallots(filtered);
    }
  }
  
  validateKeyPress(event: KeyboardEvent): boolean {
    const allowedPattern = /[A-Za-z0-9,\-'. ]/;
    const inputChar = event.key;
    
    if (!allowedPattern.test(inputChar) && inputChar.length === 1) {
      event.preventDefault();
      return false;
    }
    
    if (this.searchTerm.length >= 40 && inputChar.length === 1) {
      event.preventDefault();
      return false;
    }
    
    return true;
  }

  validatePaste(event: ClipboardEvent): void {
    event.preventDefault();
    
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    
    const pastedText = clipboardData.getData('text');
    
    const allowedPattern = /[A-Za-z0-9,\-'. ]/g;
    
    const matches = pastedText.match(allowedPattern);
    let filteredText = matches ? matches.join('') : '';
    
    const availableSpace = 40 - this.searchTerm.length;
    if (availableSpace <= 0) return;
    
    if (filteredText.length > availableSpace) {
      filteredText = filteredText.substring(0, availableSpace);
    }
    
    const selectionStart = (event.target as HTMLInputElement).selectionStart || 0;
    const selectionEnd = (event.target as HTMLInputElement).selectionEnd || 0;
    
    this.searchTerm = 
      this.searchTerm.substring(0, selectionStart) + 
      filteredText + 
      this.searchTerm.substring(selectionEnd);
  }
}
